import PriceBookListCard from "@legacy/pricebook/components/PriceBookItemListCard"

import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"

import replaceSlugs from "@utils/replaceSlugs"

import { CardList, Table, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import { CardsViewAttribute, CardsViewSortOption } from "@organisms/ObjectsView/CardsView/CardsView.types"

import { CREATE_ROUTE_MAP, PRICEBOOK_TYPE_NAME_MAP } from "@pages/Pricebook/PricebookList/PricebookList"
import {
    DiscountsTabs,
    MiscellaneousTabs,
    PartsAndMaterialsTabs,
    ServicesTabs,
    TaxRatesTabs,
} from "@pages/Pricebook/PricebookList/views/PricebookTableView/PricebookTableViewTabs"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    PRICEBOOK_LIST_CONTROLS_ACTIVE_TAB,
    PRICEBOOK_LIST_CONTROLS_KEYWORD_SEARCH,
    PRICEBOOK_LIST_CONTROLS_PHASE,
} from "@constants/storage"

import { PricebookCardsViewProps } from "./PricebookCardsView.types"

const PRICEBOOK_CARDS_VIEW_ATTRIBUTES_BY_TYPE: Record<PricebookType, CardsViewAttribute[]> = {
    service: [
        { value: "description", label: "Short Description" },
        { value: "subtype_label", label: "Subtype" },
        { value: "summary_template", label: "Summary" },
        { value: "cost", label: "Cost" },
        { value: "default_price", label: "Rate" },
        { value: "default_job_duration", label: "Est. Duration" },
        { value: "labels", label: "Labels" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    part: [
        { value: "description", label: "Short Description" },
        { value: "subtype_label", label: "Subtype" },
        { value: "manufacturer", label: "MFG" },
        { value: "part_number", label: "MFG Part #" },
        { value: "cost", label: "Cost" },
        { value: "default_price", label: "Price" },
        { value: "labels", label: "Labels" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    other: [
        { value: "description", label: "Short Description" },
        { value: "subtype_label", label: "Subtype" },
        { value: "cost", label: "Cost" },
        { value: "default_price", label: "Price" },
        { value: "labels", label: "Labels" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    discount: [
        { value: "description", label: "Short Description" },
        { value: "subtype_label", label: "Subtype" },
        { value: "summary_template", label: "Summary" },
        { value: "default_price", label: "Discount" },
        { value: "labels", label: "Labels" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    tax: [
        { value: "description", label: "Short Description" },
        { value: "summary_template", label: "Summary" },
        { value: "default_price", label: "Tax Rate" },
        { value: "labels", label: "Labels" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
}

const PRICEBOOK_CARDS_VIEW_SORT_OPTIONS_BY_TYPE: Record<PricebookType, CardsViewSortOption[]> = {
    service: [
        { value: "description", label: "Short Description" },
        { value: "status_label", label: "State" },
        { value: "subtype_label", label: "Subtype" },
        { value: "cost", label: "Cost" },
        { value: "default_price", label: "Rate" },
        { value: "default_job_duration", label: "Est. Duration" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    part: [
        { value: "description", label: "Short Description" },
        { value: "status_label", label: "State" },
        { value: "subtype_label", label: "Subtype" },
        { value: "manufacturer", label: "MFG" },
        { value: "part_number", label: "MFG Part #" },
        { value: "cost", label: "Cost" },
        { value: "default_price", label: "Price" },
        { value: "lowest_inventory_status", label: "Stock" },
        { value: "inventory_level", label: "Levels" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    other: [
        { value: "description", label: "Short Description" },
        { value: "status_label", label: "State" },
        { value: "subtype_label", label: "Subtype" },
        { value: "cost", label: "Cost" },
        { value: "default_price", label: "Price" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    discount: [
        { value: "description", label: "Short Description" },
        { value: "status_label", label: "State" },
        { value: "subtype_label", label: "Subtype" },
        { value: "default_price", label: "Discount" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
    tax: [
        { value: "description", label: "Short Description" },
        { value: "status_label", label: "State" },
        { value: "default_price", label: "Tax Rate" },
        { value: "last_modified_at", label: "Last Modified" },
    ],
}

export default function PricebookCardsView(props: PricebookCardsViewProps) {
    const { pricebookTypeName } = props
    const pricebookType = PRICEBOOK_TYPE_NAME_MAP[pricebookTypeName]
    const [createRoute] = useSlugExtractor([CREATE_ROUTE_MAP[pricebookType]])
    const { hasPermission } = useHasAccess()

    const tabsByPricebookType: Record<PricebookType, JSX.Element> = {
        service: <ServicesTabs />,
        part: <PartsAndMaterialsTabs />,
        other: <MiscellaneousTabs />,
        discount: <DiscountsTabs />,
        tax: <TaxRatesTabs />,
    }

    return (
        <Table.Provider
            activePhaseStorageKey={replaceSlugs(PRICEBOOK_LIST_CONTROLS_PHASE, {
                type: pricebookTypeName ?? "undefined",
            })}
            activeTabStorageKey={replaceSlugs(PRICEBOOK_LIST_CONTROLS_ACTIVE_TAB, {
                type: pricebookTypeName ?? "undefined",
            })}
            searchKeywordsStorageKey={replaceSlugs(PRICEBOOK_LIST_CONTROLS_KEYWORD_SEARCH, {
                type: pricebookTypeName ?? "undefined",
            })}
            listTitle={pricebookTypeName ?? ""}
            objectType="pricebook_item"
            objectCreateRoute={createRoute}
            objectIndexName="PriceBookItem"
            objectName="Pricebook Item"
            defaultActivePhase="active"
            objectTypeVariation={pricebookTypeName}
            isRowClickable={hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)}
            isEmptyStateCreateButtonVisible={hasPermission("pricebook_create_permission", PERMISSION_LEVEL.FULL)}
        >
            <CardsView.Provider
                attributes={PRICEBOOK_CARDS_VIEW_ATTRIBUTES_BY_TYPE[pricebookType]}
                sortOptions={PRICEBOOK_CARDS_VIEW_SORT_OPTIONS_BY_TYPE[pricebookType]}
            >
                <ViewBar.Root>
                    <ViewBar.Start>
                        <ViewBar.Phases>
                            <Table.Phase phase="active" title="Active" />
                            <Table.Phase phase="inactive" title="Inactive" />
                        </ViewBar.Phases>
                        <ViewBar.VerticalDivider />
                        {tabsByPricebookType[pricebookType] ?? null}
                    </ViewBar.Start>
                    <ViewBar.End>
                        <Table.SearchInput />
                        <ViewMenu />
                    </ViewBar.End>
                </ViewBar.Root>
                <CardList CardComponent={PriceBookListCard} />
            </CardsView.Provider>
        </Table.Provider>
    )
}
